import {
  type Capabilities,
  NO_CAPABILITIES,
  type VisibleContract,
} from "@contract-manager/shared";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { ApiClient } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { describeError, isSignedOutError } from "../auth/authErrors";
import { Alert } from "../components/Alert";
import { ApprovalForm } from "../components/ApprovalForm";
import { FolderPicker } from "../components/FolderPicker";
import { PermissionEditor } from "../components/PermissionEditor";
import { StatusBadge } from "../components/StatusBadge";
import { useI18n } from "../i18n/I18n";
import { isSettled, POLL_INTERVAL_MS } from "../polling";
import { hasIncompleteCoverage, presentFailureCode } from "../status";
import {
  CONTRACT_TYPE_LABEL,
  ENTITY_LABEL,
  formatNoticePeriod,
  formatRenewalTerm,
  readTrackerLabel,
} from "../tracker";

/** One `dt`/`dd` pair of a `ds-details` list. */
function Field({
  label,
  children,
  mono = false,
}: {
  label: string;
  children: ReactNode;
  mono?: boolean;
}) {
  return (
    <>
      <dt>{label}</dt>
      <dd>
        <div className={mono ? "app-mono" : undefined}>{children}</div>
      </dd>
    </>
  );
}

/** A titled card holding a label/value list. */
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="ds-card">
      <h2 className="ds-text --large">{title}</h2>
      {children}
    </section>
  );
}

function Details({ children }: { children: ReactNode }) {
  return (
    <div className="ds-details">
      <dl>{children}</dl>
    </div>
  );
}

function BackLink() {
  const { t } = useI18n();
  return (
    <Link to="/" className="ds-button --plain --small --icon-arrow-left">
      <span>{t("contracts.detail.back")}</span>
    </Link>
  );
}

/** Null is a real answer — "the contract does not say" (docs/mvp-design.md §4). */
function Stated({ value }: { value: string | null | undefined }) {
  const { t } = useI18n();
  return value === null || value === undefined ? (
    // "Not stated in the contract" is a real answer and must not read like a
    // value; it also must not read like an error.
    <span className="ds-text --muted">{t("contracts.detail.notStated")}</span>
  ) : (
    <>{value}</>
  );
}

export function ContractDetailPage({ api }: { api: ApiClient }) {
  const auth = useAuth();
  const { contractId } = useParams<{ contractId: string }>();
  const { t, locale, formatTimestamp } = useI18n();
  const [contract, setContract] = useState<VisibleContract | null>(null);
  // The failure itself, translated at render: switching language should not
  // leave the message in the old one, nor restart the polling.
  const [failure, setFailure] = useState<{ cause: unknown } | null>(null);
  const error = failure === null ? null : describeError(failure.cause, t);
  const [openingFile, setOpeningFile] = useState(false);
  // Reported by the permission editor, which asks the API for it anyway.
  // Until it answers, nobody is offered the approval form: a form that 403s
  // on submit would be worse than one that appears a moment late.
  const [callerAccess, setCallerAccess] =
    useState<Capabilities>(NO_CAPABILITIES);

  const load = useCallback(async () => {
    if (contractId === undefined) {
      return;
    }
    try {
      setContract(await api.getContract(contractId));
    } catch (caught) {
      if (isSignedOutError(caught)) {
        auth.reportSignedOut();
        return;
      }
      setFailure({ cause: caught });
    }
  }, [api, auth, contractId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Extraction is still running: keep asking until it is not. Same reasoning
  // as the list, and the same interval.
  const waiting = contract !== null && !isSettled(contract.ingestStatus);
  useEffect(() => {
    if (!waiting) {
      return;
    }
    const timer = setInterval(() => void load(), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [waiting, load]);

  const openFile = async () => {
    if (contractId === undefined) {
      return;
    }
    setOpeningFile(true);
    try {
      const file = await api.getContractFileUrl(contractId);
      window.open(file.url, "_blank", "noopener");
    } catch (caught) {
      if (isSignedOutError(caught)) {
        auth.reportSignedOut();
        return;
      }
      setFailure({ cause: caught });
    } finally {
      setOpeningFile(false);
    }
  };

  if (contract === null) {
    return (
      <div className="ds-rows --gap-medium">
        <div>
          <BackLink />
        </div>
        {error === null ? (
          <div className="ds-notice --light --center">
            <p>{t("contracts.loadingEllipsis")}</p>
          </div>
        ) : (
          <Alert tone="failed" title={t("contracts.detail.loadFailed")}>
            {error}
          </Alert>
        )}
      </div>
    );
  }

  const failureKey = presentFailureCode(contract.failureCode);
  const coverage = contract.pageCoverage;
  const extracted = contract.extracted;
  const reviewable =
    contract.ingestStatus === "EXTRACTED" &&
    contract.access === "full" &&
    callerAccess.canEdit;

  return (
    <div className="ds-rows --gap-medium">
      <div>
        <BackLink />
      </div>

      <div className="ds-cols --spaced --top --gap-small">
        <div className="ds-rows --gap-xsmall app-shrink">
          <h1 className="app-break">{contract.originalFileName}</h1>
          <div className="ds-cols --middle --gap-small">
            <StatusBadge status={contract.ingestStatus} />
            <span className="ds-text --muted app-mono">
              {contract.contractId}
            </span>
          </div>
        </div>
        <div>
          {/* A contract can be listed so that it can be asked for, without the
              file itself being readable. Offering a button that 403s would be
              worse than not offering one. */}
          {contract.access === "full" && (
            <button
              type="button"
              className={`ds-button --primary --icon-external-link${openingFile ? " --loading" : ""}`}
              disabled={openingFile}
              // SDS hides the label of a loading button; keep its name.
              aria-label={
                openingFile ? t("contracts.detail.issuingUrl") : undefined
              }
              onClick={() => void openFile()}
            >
              <span>
                {openingFile
                  ? t("contracts.detail.issuingUrl")
                  : t("contracts.detail.openPdf")}
              </span>
            </button>
          )}
        </div>
      </div>

      {(error !== null ||
        failureKey !== null ||
        waiting ||
        contract.access === "discoverable" ||
        hasIncompleteCoverage(coverage)) && (
        <div className="ds-rows --gap-small">
          {error !== null && <Alert tone="failed">{error}</Alert>}

          {contract.access === "discoverable" && (
            <Alert tone="info" title={t("contracts.detail.discoverableTitle")}>
              {t("contracts.detail.discoverableBody")}
            </Alert>
          )}

          {failureKey !== null && (
            <Alert tone="failed" title={t("contracts.detail.failedTitle")}>
              {t(failureKey)}
            </Alert>
          )}

          {waiting && (
            <Alert tone="info">{t("contracts.detail.extracting")}</Alert>
          )}

          {hasIncompleteCoverage(coverage) && coverage !== undefined && (
            <Alert tone="warning" title={t("contracts.detail.coverageTitle")}>
              {t("contracts.detail.coverageBody", {
                expected: coverage.expectedPageCount,
                detected: coverage.detectedPageCount,
                missing: coverage.missingPageRanges.join(", "),
              })}
            </Alert>
          )}
        </div>
      )}

      <Section title={t("contracts.detail.folderSection")}>
        <Details>
          <Field label={t("contracts.detail.folder")}>
            <FolderPicker
              api={api}
              contractId={contract.contractId}
              folderId={contract.folderId}
              // Patch locally rather than re-fetching: the answer already
              // carries the new folder, and a reload here would fight the
              // extraction polling for the same record.
              onChanged={(next) =>
                setContract((current) =>
                  current === null
                    ? current
                    : {
                        ...current,
                        ...(next === undefined
                          ? { folderId: undefined }
                          : { folderId: next }),
                      },
                )
              }
            />
          </Field>
        </Details>
        <PermissionEditor
          api={api}
          resourceType="CONTRACT"
          resourceId={contract.contractId}
          onCallerAccess={setCallerAccess}
        />
      </Section>

      <Section title={t("contracts.detail.extractedSection")}>
        {extracted && reviewable ? (
          <ApprovalForm
            key={contract.contractId}
            api={api}
            contractId={contract.contractId}
            extracted={extracted}
            // The answer is the approved record; no need to fetch it again.
            onApproved={setContract}
            onConflict={(cause) => {
              setFailure({ cause });
              void load();
            }}
          />
        ) : extracted ? (
          <Details>
            <Field label={t("contracts.detail.field.title")}>
              {extracted.title}
            </Field>
            <Field label={t("contracts.detail.field.type")}>
              {readTrackerLabel(CONTRACT_TYPE_LABEL[extracted.contractType], t)}
            </Field>
            <Field label={t("contracts.detail.field.counterparty")}>
              {extracted.counterparty}
            </Field>
            <Field label={t("contracts.detail.field.shortName")}>
              <Stated value={extracted.counterpartyShortName} />
            </Field>
            <Field label={t("contracts.detail.field.entity")}>
              {readTrackerLabel(ENTITY_LABEL[extracted.contractingEntity], t)}
            </Field>
            <Field label={t("contracts.detail.field.amount")} mono>
              {extracted.amount === null ? (
                <Stated value={null} />
              ) : (
                `${extracted.amount.value.toLocaleString(locale)} ${extracted.amount.currency}`
              )}
            </Field>
            <Field label={t("contracts.detail.field.effectiveDate")} mono>
              <Stated value={extracted.effectiveDate} />
            </Field>
            <Field label={t("contracts.detail.field.endDate")} mono>
              <Stated value={extracted.endDate} />
            </Field>
            <Field label={t("contracts.detail.field.autoRenewal")}>
              {extracted.autoRenewal === null ? (
                <Stated value={null} />
              ) : extracted.autoRenewal ? (
                t("contracts.detail.yes")
              ) : (
                t("contracts.detail.no")
              )}
            </Field>
            <Field label={t("contracts.detail.field.renewalTerm")}>
              <Stated value={formatRenewalTerm(extracted.renewalTermMonths)} />
            </Field>
            <Field label={t("contracts.detail.field.noticePeriod")}>
              <Stated
                value={formatNoticePeriod(extracted.renewalNoticePeriodDays)}
              />
            </Field>
            <Field label={t("contracts.detail.field.noticeDeadline")} mono>
              <Stated value={extracted.renewalNoticeDeadline} />
            </Field>
            <Field label={t("contracts.detail.field.lifecycle")}>
              {contract.lifecycleStatus === "UNKNOWN" ? (
                <Stated value={null} />
              ) : (
                contract.lifecycleStatus
              )}
            </Field>
            {contract.approvedBy !== undefined && (
              <Field label={t("contracts.detail.field.approvedBy")}>
                {contract.approvedBy}
              </Field>
            )}
            {contract.approvedAt !== undefined && (
              <Field label={t("contracts.detail.field.approvedAt")} mono>
                {formatTimestamp(contract.approvedAt)}
              </Field>
            )}
          </Details>
        ) : (
          <p className="ds-text --muted">
            {waiting
              ? t("contracts.detail.pendingExtraction")
              : t("contracts.detail.noExtraction")}
          </p>
        )}
      </Section>

      <Section title={t("contracts.detail.fileSection")}>
        <Details>
          <Field label={t("contracts.detail.field.uploadedBy")}>
            {contract.uploadedBy}
          </Field>
          <Field label={t("contracts.detail.field.uploadedAt")} mono>
            {formatTimestamp(contract.uploadedAt)}
          </Field>
          <Field label={t("contracts.detail.field.pageCount")} mono>
            {contract.pageCount === null ? (
              <Stated value={null} />
            ) : (
              `${contract.pageCount}`
            )}
          </Field>
          <Field label={t("contracts.detail.field.updatedAt")} mono>
            {formatTimestamp(contract.updatedAt)}
          </Field>
        </Details>
      </Section>
    </div>
  );
}
